import * as React from "react";
import { flushSync } from "react-dom";
import { useApiKeysContext } from "./api-keys-context.js";

interface ApiKeysSearchContextType {
  inputRef: React.RefObject<HTMLInputElement | null>;
  clearSearch: () => void;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

const ApiKeysSearchContext = React.createContext<
  ApiKeysSearchContextType | undefined
>(undefined);
ApiKeysSearchContext.displayName = "ApiKeysSearchContext";

export const ApiKeysSearchProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const {
    state: { searchQuery },
    dispatch,
  } = useApiKeysContext();

  const [searchValue, setSearchValue] = React.useState(searchQuery || "");

  const clearSearch = React.useCallback(() => {
    flushSync(() => {
      setSearchValue("");
      dispatch({ type: "FILTER_BY_SEARCH", searchQuery: "" });
    });
    inputRef.current?.focus();
  }, [dispatch]);

  return (
    <ApiKeysSearchContext.Provider
      value={{ inputRef, clearSearch, searchValue, setSearchValue }}
    >
      {children}
    </ApiKeysSearchContext.Provider>
  );
};

export const useApiKeysSearchContext = () => {
  const context = React.useContext(ApiKeysSearchContext);
  if (!context) {
    throw new Error(
      "useApiKeysSearchContext must be used within an ApiKeysSearchProvider",
    );
  }
  return context;
};
